<template>
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <template #title>
        <span class="text-primary">{{ t("onboarding.internalInviteModal.title") }}</span>
      </template>

      <v-card-text class="pb-0">
        <p v-if="!successMessage" class="mb-4">
          {{ t("onboarding.internalInviteModal.description") }}
        </p>
        <p v-else class="mb-4">{{ successMessage }}</p>
      </v-card-text>

      <v-form class="px-6 pb-6">
        <v-text-field
          v-if="!successMessage"
          v-model.trim="email"
          class="w-100"
          :label="t('onboarding.internalInviteModal.emailLabel')"
          type="email"
          autocapitalize="none"
          autocomplete="email"
          clearable
          :error-messages="
            (v$.email.$dirty &&
              v$.email.$invalid &&
              v$.email.$silentErrors[0].$message.toString()) ||
            ''
          "
        />
        <div class="w-100 d-flex justify-space-between align-center mt-4">
          <v-btn-nd @click="dialog = false">
            {{ t("form.close") }}
          </v-btn-nd>
          <v-btn
            v-if="!successMessage"
            class="bg-primary on-primary"
            :loading="false"
            :disabled="email.length === 0 || v$.$errors.length > 0"
            @click="handleSubmit"
          >
            {{ t("onboarding.internalInviteModal.submitButton") }}
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { helpers, required, email as emailValidator } from "@vuelidate/validators";
import { AxiosError } from "axios";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useYetipayApiNotAuthenticated } from "@/composables/useYetipayApi";
import { useOnboardingStore } from "@/store/onboarding";

const { t } = useI18n();
const { api } = useYetipayApiNotAuthenticated();

const onboardingStore = useOnboardingStore();

const dialog = defineModel<boolean>({ required: true });

const email = ref<string>("");
const isLoading = ref<boolean>(false);

const successMessage = ref<string | null>(null);

const v$ = useVuelidate(
  {
    email: {
      $autoDirty: true,
      required: helpers.withMessage(t("form.required"), required),
      email: helpers.withMessage(t("form.emailInvalid"), emailValidator),
    },
  },
  reactive({
    email,
  }),
);

const handleSubmit = async () => {
  isLoading.value = true;
  try {
    const response = await api.createDraftMerchantInviteToken(
      onboardingStore.draftMerchant!.email,
      onboardingStore.draftMerchant!.dealShortCode,
      {
        recipientEmail: email.value,
      },
    );
    if (response.data.userType === "existing") {
      successMessage.value = t("onboarding.internalInviteModal.existingUserSuccess");
      onboardingStore.clearDraftMerchant();
    } else {
      successMessage.value = t("onboarding.internalInviteModal.newUserSuccess");
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      onboardingStore.setError(error.response?.data.message ?? error.message);
    } else {
      onboardingStore.setError(t("onboarding.internalInviteModal.error"));
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
